import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, Loader2Icon } from 'lucide-react';
import { FormField } from './FormField';
type Values = {
  name: string;
  title: string;
  email: string;
  goals: string;
};
type Errors = Partial<Record<keyof Values, string>>;
const initial: Values = {
  name: '',
  title: '',
  email: '',
  goals: ''
};
export function ConsultationForm() {
  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const update = (key: keyof Values) => (value: string) => {
    setValues(v => ({
      ...v,
      [key]: value
    }));
    if (errors[key]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = validate(values);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 1200);
  };
  const reset = () => {
    setValues(initial);
    setStatus('idle');
  };
  return <div className="rounded-sm border border-hairline bg-white p-8 shadow-[0_1px_2px_rgba(27,42,74,0.04),0_12px_40px_-12px_rgba(27,42,74,0.12)] sm:p-10" data-id="element-15" data-mp="3fi94k8narz36">
      <AnimatePresence mode="wait" initial={false} data-id="element-16" data-mp="3pecg2541ha2s">
        {status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="py-10 text-center" role="status" data-id="element-17" data-mp="78zfiha9dypa">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold-tint text-gold-dark" data-id="element-18" data-mp="bzpk3sav1xix">
              <CheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-19" data-mp="q0ijaqc3xgsd" />
            </span>
            <h3 className="mt-6 font-serif text-2xl text-navy" data-id="element-20" data-mp="l1p2anxm37ng">Thank you, {values.name.split(' ')[0]}.</h3>
            <p className="mx-auto mt-3 max-w-sm text-steel" data-id="element-21" data-mp="3etottbr3by7o">
              Your consultation request has been received. Dr. Vecchione will respond personally within one
              business day to schedule your strategy session.
            </p>
            <button type="button" onClick={reset} className="mt-8 text-sm font-semibold text-navy underline decoration-gold underline-offset-4 hover:decoration-2" data-id="element-22" data-mp="3nrh6e85fgrrd">
              Submit another request
            </button>
          </motion.div> : <motion.form key="form" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onSubmit={handleSubmit} noValidate aria-label="Consultation request" data-id="element-23" data-mp="bc6jmxnuif43">
            <h2 className="font-serif text-2xl text-navy" data-id="element-24" data-mp="2h4bxsfyoqrwf">Request a Confidential Consultation</h2>
            <p className="mt-2 text-sm text-steel" data-id="element-25" data-mp="29lvz1ys9e2us">All inquiries are held in strict confidence.</p>
            <div className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-26" data-mp="24u43tay5r8yb">
              <FormField id="name" label="Full name" value={values.name} onChange={update('name')} error={errors.name} autoComplete="name" placeholder="Jane Morrison" data-id="element-27" data-mp="24sjd5weysa5q" />
              <FormField id="title" label="Current title" value={values.title} onChange={update('title')} error={errors.title} autoComplete="organization-title" placeholder="Senior Vice President" data-id="element-28" data-mp="1vv91wb25cl0t" />
            </div>
            <div className="mt-6" data-id="element-29" data-mp="2nlwwf49bes5i">
              <FormField id="email" label="Email address" type="email" value={values.email} onChange={update('email')} error={errors.email} autoComplete="email" placeholder="jane@company.com" data-id="element-30" data-mp="2jja12tdfqp3k" />
            </div>
            <div className="mt-6" data-id="element-31" data-mp="184wjg699z5vz">
              <FormField id="goals" label="Target goals" multiline value={values.goals} onChange={update('goals')} error={errors.goals} placeholder="E.g., positioning for a COO role, preparing a board dossier, or navigating a sector transition." data-id="element-32" data-mp="185y5t6vqret0" />
            </div>
            <button type="submit" disabled={status === 'submitting'} className="mt-8 inline-flex h-12 w-full items-center justify-center gap-2 rounded-sm bg-navy px-6 text-sm font-semibold tracking-wide text-white transition-colors duration-150 hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-33" data-mp="2vt8738frkusa">
              {status === 'submitting' ? <>
                  <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-34" data-mp="30r9lhg28w5xf" />
                  Sending request…
                </> : <>
                  Schedule Executive Strategy Session
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-35" data-mp="1ubpi4nl74hr2" />
                </>}
            </button>
          </motion.form>}
      </AnimatePresence>
    </div>;
}
function validate(v: Values): Errors {
  const errors: Errors = {};
  if (!v.name.trim()) errors.name = 'Please enter your name.';
  if (!v.title.trim()) errors.title = 'Please enter your current title.';
  if (!v.email.trim()) errors.email = 'Please enter your email address.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) errors.email = 'Please enter a valid email address.';
  if (v.goals.trim().length < 10) errors.goals = 'Briefly describe your goals (at least 10 characters).';
  return errors;
}